import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import "./index.scss";
import ChartTable from "../../../components/chart-table";
import { Box, Grid, Typography } from "@mui/material";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const OemModelWiseSummary = ({ responseData }) => {
  const getRandomColor = () =>
    `#${Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0")}`;

  // ✅ Each model becomes a dataset, each OEM is a label
  const processModelWiseData = (data) => {
    const oems = Object.keys(data || {});
    const allModels = new Set();

    // Collect all unique models
    Object.values(data || {}).forEach((models) => {
      Object.keys(models).forEach((model) => allModels.add(model));
    });

    const datasets = Array.from(allModels).map((model) => {
      const modelData = oems.map((oem) => data[oem]?.[model] || 0);
      return {
        label: model,
        data: modelData,
        backgroundColor: getRandomColor(),
        barThickness: 20,
      };
    });

    return {
      labels: oems, // OEMs on Y-axis
      datasets,
    };
  };

  const processTotalCountData = (data) => {
    const labels = Object.keys(data || {});
    const values = labels.map((oem) =>
      Object.values(data[oem] || {}).reduce((a, b) => a + b, 0)
    );

    return {
      labels,
      datasets: [
        {
          label: "Total Count",
          data: values,
          backgroundColor: labels.map(() => getRandomColor()),
          barThickness: 20,
        },
      ],
    };
  };

  const modelWiseData = processModelWiseData(responseData);
  const totalCountData = processTotalCountData(responseData);

  const chartOptions = {
    indexAxis: "y", // Make it horizontal
    responsive: true,
    plugins: {
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${tooltipItem.raw}`,
        },
      },
      legend: {
        position: "top",
      },
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        grid: { display: false },
        ticks: { display: true },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { display: true },
      },
    },
  };

  const totalCountChartOptions = {
    responsive: true,
    plugins: {
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${tooltipItem.raw}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
      },
    },
  };

  // 📊 Table headers: ["OEM", model1, model2, ...]
  const modelTableHeaders = [
    "OEM",
    ...modelWiseData.datasets.map((ds) => ds.label),
  ];

  // 📊 Table rows: each OEM, with its model counts from datasets
  const modelTableRows = modelWiseData.labels.map((oem, oemIndex) => {
    const row = [oem];
    modelWiseData.datasets.forEach((dataset) => {
      row.push(dataset.data[oemIndex] || 0);
    });
    return row;
  });

  // ✅ Correct grand total (sum of each model column)
  const grandTotalRow = ["Grand Total"];
  modelWiseData.datasets.forEach((dataset) => {
    const total = dataset.data.reduce((a, b) => a + b, 0);
    grandTotalRow.push(total);
  });

  const totalTableHeaders = ["OEM", "Total Count"];
  const totalTableRows = totalCountData.labels.map((label, index) => [
    label,
    totalCountData.datasets[0].data[index],
  ]);
  const totalCountGrandTotal = totalCountData.datasets[0].data.reduce(
    (a, b) => a + b,
    0
  );
  const totalGrandTotalRow = ["Grand Total", totalCountGrandTotal];

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item md={9}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            OEM vs Engine Model Wise
          </Typography>
          <Bar data={modelWiseData} options={chartOptions} height={100} />
          <ChartTable
            headers={modelTableHeaders}
            rows={[...modelTableRows, grandTotalRow]}
          />
        </Grid>
        <Grid item md={3}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            OEM vs Total Count
          </Typography>

          <Bar
            data={totalCountData}
            options={totalCountChartOptions}
            height={310}
          />

          <ChartTable
            headers={totalTableHeaders}
            rows={[...totalTableRows, totalGrandTotalRow]}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OemModelWiseSummary;
